import { useForm } from 'react-hook-form';
import {
  ApiAccount,
  ApiBeneficiary,
  ApiStandingOrder,
  ApiStandingOrderDestination,
} from '../../../openapi';
import {
  Button,
  FormContentIsland,
  HookForm,
  PercentField,
  Stack,
  Tab,
  TabsField,
} from '@razz-team/ui-web';
import { useCallback, useMemo } from 'react';
import { useCreateStandingOrderDestinationMutation } from '../../../api/standing-orders';
import { useTranslation } from 'react-i18next';
import { useFormError } from '../../../hooks/useFormError';
import { AccountField } from '../../Field/AccountField';
import { BeneficiaryField } from '../../Field/BeneficiaryField';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';
import { formatPercentValue } from '@razz-team/utils';

type FormValues = {
  type: 'OwnAccount' | 'External';
  accountId: string | null;
  beneficiaryId: string | null;
  ratio: number;
};

export function AddStandingOrderDestination({
  order,
  destinations,
  accounts,
  beneficiaries,
  maxRatio,
}: {
  order: ApiStandingOrder;
  destinations: ApiStandingOrderDestination[];
  accounts: ApiAccount[];
  beneficiaries: ApiBeneficiary[];
  maxRatio: number;
}) {
  const { t } = useTranslation();

  const form = useForm<FormValues>({
    disabled: maxRatio === 0,
    defaultValues: {
      type: 'OwnAccount',
      accountId: null,
      beneficiaryId: null,
    },
  });

  const [type] = form.watch(['type']);
  const idempotencyKey = useIdempotencyKey();
  const mutation = useCreateStandingOrderDestinationMutation({
    orderId: order.id,
    idempotencyKey,
  });
  useFormError(form, 'type', mutation.error);

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      if (mutation.isPending) return;
      if (values.type === 'OwnAccount' && values.accountId) {
        await mutation.mutateAsync({
          destination: {
            type: 'OwnAccount',
            orderId: order.id,
            ratio: values.ratio,
            accountId: values.accountId,
          },
        });
        form.reset();
      } else if (values.type === 'External' && values.beneficiaryId) {
        await mutation.mutateAsync({
          destination: {
            type: 'External',
            orderId: order.id,
            ratio: values.ratio,
            beneficiaryId: values.beneficiaryId,
          },
        });
        form.reset();
      }
    },
    [mutation, order.id, form],
  );

  const unusedAccounts = useMemo(
    () =>
      accounts.filter(
        (account) =>
          !destinations.some(
            (destination) =>
              destination.type === 'OwnAccount' && destination.accountId === account.id,
          ),
      ),
    [accounts, destinations],
  );

  const unusedBeneficiaries = useMemo(
    () =>
      beneficiaries.filter(
        (beneficiary) =>
          !destinations.some(
            (destination) =>
              destination.type === 'External' &&
              destination.beneficiaryId === beneficiary.beneficiaryId,
          ),
      ),
    [beneficiaries, destinations],
  );

  return (
    <Stack spacing={2} width="100%" component={HookForm} form={form} onSubmit={handleSubmit}>
      <FormContentIsland title={t('Add destination')}>
        <TabsField name="type" label={t('Destination type')} required>
          <Tab label={t('Own account')} value="OwnAccount" />
          <Tab label={t('Transfer')} value="External" />
        </TabsField>
        {type === 'OwnAccount' && (
          <AccountField name="accountId" label={t('Account')} accounts={unusedAccounts} />
        )}
        {type === 'External' && (
          <BeneficiaryField
            name="beneficiaryId"
            label={t('Beneficiary')}
            beneficiaries={unusedBeneficiaries}
          />
        )}
        <PercentField
          name="ratio"
          label={t('Ratio')}
          max={maxRatio}
          helperText={t('You can allocate from 0.01% to {{ max }}% of the order', {
            max: formatPercentValue(maxRatio),
          })}
          required
        />
      </FormContentIsland>
      <Button type="submit" size="large" color="primary" loading={mutation.isPending} fullWidth>
        {t('Add')}
      </Button>
    </Stack>
  );
}
